import { useEffect } from "react";
import { useNavigate } from "react-router";
import { PageHeader } from "@/components/page-header";
import { DocButton } from "@/components/ui/doc-button";
import { useMe } from "@/lib/hooks/use-clients";
import { useWebhooks } from "@/lib/hooks/use-webhooks";
import { useT } from "@/lib/i18n/use-t";
import { hasPermission, type Permission } from "@/lib/permissions";
import { T } from "./page.i18n";
import { WebhookWrapper } from "./webhook-wrapper";

export default function WebhooksPage() {
  const navigate = useNavigate();
  const t = useT(T);
  const { me, isLoading: meLoading } = useMe();

  // QL-443 — webhook config and delivery payloads are developer-only (the
  // backend enforces the same on /internal/webhooks).
  const userPermissions = (me?.permissions || ["advisor"]) as Permission[];
  const canAccess = hasPermission(userPermissions, "webhooks");

  const { webhooks, isLoading, mutate } = useWebhooks({ enabled: !meLoading && canAccess });

  useEffect(() => {
    if (!meLoading && !canAccess) {
      navigate("/", { replace: true });
    }
  }, [meLoading, canAccess, navigate]);

  if (meLoading || !canAccess || isLoading) return null;

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={[{ label: t.settings, href: "/settings" }, { label: t.webhooks }]}
        title={t.webhooks}
        description={t.pageDescription}
        actions={
          <DocButton docSlug="webhooks" variant="outline" size="sm">
            {t.documentation}
          </DocButton>
        }
      />

      <WebhookWrapper webhooks={webhooks} mutate={mutate} />
    </div>
  );
}
